import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand, useProductName } from "@/context/BrandContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, Eye, EyeSlash } from "@phosphor-icons/react";
import CaptchaWidget, { resetCaptcha } from "@/components/CaptchaWidget";

function BrandMark({ brand, productName, size = "lg", light = true }) {
  const box = size === "lg" ? "w-11 h-11" : "w-10 h-10";
  const icon = size === "lg" ? "w-6 h-6" : "w-5 h-5";
  const titleCls = size === "lg" ? "text-xl" : "text-lg";
  const src = light
    ? (brand.login_logo_url || brand.logo_url)
    : (brand.logo_url || brand.login_logo_url);
  const slogan = brand.slogan || brand.tagline || "";
  const sloganCls = light
    ? "text-primary-foreground/70"
    : "text-muted-foreground";

  const textBlock = productName ? (
    <div className="leading-tight min-w-0">
      <p className={`font-display font-bold tracking-tight ${titleCls}`}>{productName}</p>
      {slogan ? (
        <p className={`mt-0.5 text-[10px] uppercase tracking-[0.22em] ${sloganCls}`}>{slogan}</p>
      ) : null}
    </div>
  ) : (
    <div className="space-y-1.5" aria-hidden>
      <div className={`h-5 w-24 rounded ${light ? "bg-white/20" : "bg-muted"} animate-pulse`} />
      <div className={`h-2.5 w-28 rounded ${light ? "bg-white/15" : "bg-muted/70"} animate-pulse`} />
    </div>
  );

  if (src) {
    return (
      <div className="flex items-center gap-3 relative">
        <img src={src} alt={productName || "Logo"} className={`${box} object-contain`} />
        {textBlock}
      </div>
    );
  }
  if (!productName) {
    return (
      <div className="flex items-center gap-3 relative" aria-hidden>
        <div className={`${box} rounded-xl ${light ? "bg-white/20" : "bg-muted"} animate-pulse`} />
        {textBlock}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 relative">
      <div className={`${box} rounded-xl ${light ? "bg-white/15" : "bg-primary text-primary-foreground"} grid place-items-center`}>
        <Phone className={icon} weight="fill" />
      </div>
      {textBlock}
    </div>
  );
}

export default function Signup() {
  const { signup, user, ready } = useAuth();
  const { brand } = useBrand();
  const productName = useProductName();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);

  const onCaptcha = useCallback((token) => {
    setCaptchaToken(token);
  }, []);
  const onCaptchaEnabled = useCallback((enabled) => {
    setCaptchaRequired(!!enabled);
    if (!enabled) setCaptchaToken(null);
  }, []);

  useEffect(() => {
    if (ready && user) navigate("/dashboard", { replace: true });
  }, [ready, user, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (captchaRequired && !captchaToken) {
      setError("Please complete the captcha.");
      return;
    }
    setLoading(true);
    try {
      const result = await signup({
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        password: form.password,
        captcha_token: captchaToken || undefined,
      });
      if (result?.requires_verification) {
        setDone({
          email: result.email || form.email.trim(),
          message: result.message || "Check your email to activate your account.",
        });
        return;
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err) || err.message);
      resetCaptcha();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="signup-page">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 -left-16 w-80 h-80 rounded-full bg-black/10 blur-3xl" />
        <BrandMark brand={brand} productName={productName} />
        <div className="relative">
          <h1 className="text-4xl font-display font-bold leading-tight tracking-tight">
            Launch your first AI voice campaign in minutes.
          </h1>
          <p className="mt-4 text-primary-foreground/80 max-w-md">
            Create a customer account, add cash, buy concurrent channels and start
            running human-like outbound conversations at scale — all on your own SIP
            infrastructure.
          </p>
        </div>
        <p className="text-xs text-primary-foreground/60 relative">
          © {new Date().getFullYear()} {productName || "\u00a0"} · Control Plane
        </p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <BrandMark brand={brand} productName={productName} size="sm" light={false} />
          </div>
          {done ? (
            <div data-testid="signup-verify-pending">
              <h2 className="text-2xl font-display font-semibold tracking-tight">Check your email</h2>
              <p className="text-sm text-muted-foreground mt-1.5">
                We sent an activation link to <span className="font-medium text-foreground">{done.email}</span>.
              </p>
              <p className="text-sm text-muted-foreground mt-4">{done.message}</p>
              <p className="text-sm text-muted-foreground mt-2">
                After you click the link your {productName || "account"} will be ready. An admin or reseller can also activate you manually.
              </p>
              <Link
                to="/login"
                data-testid="signup-to-login"
                className="mt-8 inline-flex h-11 w-full items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-display font-semibold tracking-tight">Create your account</h2>
              <p className="text-sm text-muted-foreground mt-1.5">
                {productName ? `Get started with ${productName} — no card required.` : "Get started — no card required."}
              </p>

              <form onSubmit={submit} className="mt-8 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" value={form.name} required onChange={set("name")}
                    data-testid="signup-name" placeholder="Jane Doe" className="h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input id="company" value={form.company} required onChange={set("company")}
                    data-testid="signup-company" placeholder="Acme Inc." className="h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={form.email} required onChange={set("email")}
                    data-testid="signup-email" placeholder="you@company.com" className="h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input id="password" type={show ? "text" : "password"} value={form.password} required
                      onChange={set("password")} data-testid="signup-password"
                      placeholder="At least 8 characters" className="h-11 pr-10" />
                    <button type="button" onClick={() => setShow((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {show ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <CaptchaWidget onChange={onCaptcha} onEnabled={onCaptchaEnabled} />
                {error && (
                  <p className="text-sm text-destructive" data-testid="signup-error">{error}</p>
                )}
                <Button type="submit" disabled={loading} data-testid="signup-submit"
                  className="w-full h-11 rounded-lg font-medium">
                  {loading ? "Creating account…" : "Create account"}
                </Button>
              </form>

              <p className="text-sm text-muted-foreground mt-6 text-center">
                Already have an account?{" "}
                <Link to="/login" data-testid="signup-to-login" className="text-primary font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
